import { toast } from 'sonner'
import { useState, useEffect } from 'react'
// API & Types
import { fError } from '@/utils/format-error'
import axios, { endpoints } from '@/lib/axios'
// Components
import { useBoolean } from '@/hooks/use-boolean'
import { useDebounceText } from '@/hooks/use-debounce-text'
import CustomPagination from '@/components/pagination/custom-pagination'
import LoadingSection from '@/components/loading-indicator/loading-section'

import UserVerifyTable from '../user-verify-table'
import UserVerifyFilter from '../user-verify-filter'
import { UserVerifyFilters, UserVerifyItem } from '../types'

// Constants
const PAGE_SIZE = 10

export default function UserVerifyView() {
  const loading = useBoolean(true)

  // State
  const [userVerify, setUserVerify] = useState<UserVerifyItem[]>([])
  const [pageMeta, setPageMeta] = useState({
    total: 0,
    page: 0,
    limit: 0,
  })

  const [search, setSearch] = useState('')

  const [filters, setFilters] = useState<UserVerifyFilters>({
    page: 1,
    limit: PAGE_SIZE,
    orderBy: 'createdAt:desc',
    isAllowSearchOnlyMemberCode: true,
  })

  const debounceSearch = useDebounceText(search)

  // Fetch orders data
  const fetchUserVerifies = async () => {
    try {
      loading.onTrue()

      const params: Record<string, unknown> = {
        ...filters,
        ...(filters?.role && { role: filters.role }),
        ...(typeof filters?.isProfileVerified === 'boolean' && {
          isProfileVerified: filters.isProfileVerified,
        }),
      }
      if (debounceSearch && debounceSearch.trim() !== '') {
        params.search = debounceSearch
      } else {
        delete params.search
      }

      const { data } = await axios.get(endpoints.users.list, {
        params,
      })

      setUserVerify(data.items)
      setPageMeta({
        total: data.total,
        page: filters.page,
        limit: filters.limit,
      })
    } catch (error) {
      console.error('Error fetching orders:', error)
      toast.error(fError(error))
      setUserVerify([])
    } finally {
      loading.onFalse()
    }
  }

  // Reset page to 1 when search changes
  useEffect(() => {
    if (debounceSearch !== undefined) {
      setFilters((prev) => ({ ...prev, page: 1 }))
    }
  }, [debounceSearch])

  // Initial load และ refetch เมื่อ filters เปลี่ยน
  useEffect(() => {
    fetchUserVerifies()
  }, [filters, debounceSearch])

  // Handle filter changes
  const handleFiltersChange = (
    newFilters: Partial<UserVerifyFilters | string>,
    isSearch?: boolean,
  ) => {
    if (isSearch && typeof newFilters === 'string') {
      setSearch(newFilters)
    } else if (typeof newFilters !== 'string') {
      setFilters((prev) => ({ ...prev, ...newFilters }))
    }
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleUserVerifyUpdate = async (updated: UserVerifyItem) => {
    if (updated) {
      try {
        await axios.patch(endpoints.users.update(updated.uid), {
          isProfileVerified: updated.isProfileVerified,
        })

        setUserVerify((prev) =>
          prev.map((item) => (item.uid === updated.uid ? updated : item)),
        )
      } finally {
        toast.success('อัพเดทข้อมูลสำเร็จ')
      }
    }
  }

  return (
    <div className="w-full">
      <LoadingSection loading={false}>
        <div className="space-y-6 mb-6">
          {/* Filter Section */}
          <UserVerifyFilter
            search={search}
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />

          {/* Table Section */}
          <UserVerifyTable
            userVerify={userVerify}
            loading={loading.value}
            onUserVerifyUpdate={handleUserVerifyUpdate}
          />

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>
    </div>
  )
}
